/**
 * Markdown — safe, themed renderer for AI/assistant prose.
 *
 * The Tonnie CoPilot (and any other assistant surface) streams GitHub-flavored
 * markdown — headings, bold, lists, tables, code. This renders it with
 * `react-markdown` (no `dangerouslySetInnerHTML`, so it's XSS-safe by default)
 * and a compact, token-driven element map that reads well inside a chat bubble
 * in both light and dark themes. Partial markdown mid-stream degrades
 * gracefully (an unclosed `**` just renders as text until it closes).
 */

import { memo, type ReactNode, useMemo } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { cn } from '../lib/utils';

/**
 * Split a still-streaming markdown string into a STABLE committed prefix and the
 * LIVE tail (the last, still-arriving block). Blank lines (`\n\n`) always
 * separate complete GFM blocks — EXCEPT inside a fenced code block, which may
 * contain blank lines — so we only split at a boundary where the ``` fence count
 * so far is balanced. Freezing the committed blocks means only the small tail
 * re-parses per token, which kills the per-token reflow ("renders raw, then
 * reflows into styled markup") without ever splitting a list/table/code block.
 */
function splitStreamingMarkdown(src: string): { stable: string; tail: string } {
  let lastSafe = -1;
  const re = /\n[ \t]*\n/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(src)) !== null) {
    const idx = m.index + m[0].length;
    const fenceCount = (src.slice(0, idx).match(/```/g) ?? []).length;
    if (fenceCount % 2 === 0) lastSafe = idx;
  }
  if (lastSafe <= 0) return { stable: '', tail: src };
  return { stable: src.slice(0, lastSafe), tail: src.slice(lastSafe) };
}

/**
 * One parsed markdown fragment. Memoized on its content string so a frozen
 * committed prefix does NOT re-parse on every streamed token — only the fragment
 * whose `content` actually changed (the live tail) re-renders.
 */
const MarkdownBody = memo(function MarkdownBody({
  content,
  components,
}: {
  content: string;
  components: Components;
}) {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {content}
    </ReactMarkdown>
  );
});

const COMPONENTS: Components = {
  h1: ({ children }) => (
    <h1 className="text-base font-semibold mt-3 mb-1.5 first:mt-0">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-sm font-semibold mt-3 mb-1.5 first:mt-0">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-sm font-semibold mt-2.5 mb-1 first:mt-0">{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className="text-xs font-semibold uppercase tracking-wide mt-2.5 mb-1 first:mt-0">
      {children}
    </h4>
  ),
  p: ({ children }) => <p className="my-1.5 first:mt-0 last:mb-0 leading-relaxed">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => (
    <ul className="my-1.5 ml-4 list-disc space-y-1 marker:text-muted-foreground">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="my-1.5 ml-4 list-decimal space-y-1 marker:text-muted-foreground">{children}</ol>
  ),
  li: ({ children }) => <li className="leading-relaxed pl-0.5">{children}</li>,
  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary-accessible underline underline-offset-2 hover:no-underline"
    >
      {children}
    </a>
  ),
  code: ({ className, children }) => {
    // Block code carries a `language-*` class from the fence; inline code doesn't.
    const isBlock = /language-/.test(className ?? '');
    if (isBlock) {
      return <code className={cn('font-mono text-xs', className)}>{children}</code>;
    }
    return (
      <code className="rounded bg-foreground/10 px-1 py-0.5 font-mono text-[0.85em]">
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="my-2 overflow-x-auto rounded-lg bg-foreground/[0.06] p-3 text-xs leading-relaxed">
      {children}
    </pre>
  ),
  blockquote: ({ children }) => (
    <blockquote className="my-2 border-l-2 border-border pl-3 italic">{children}</blockquote>
  ),
  hr: () => <hr className="my-3 border-border" />,
  table: ({ children }) => (
    <div className="my-2 overflow-x-auto">
      <table className="w-full border-collapse text-xs">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border border-border bg-foreground/[0.04] px-2 py-1 text-left font-semibold">
      {children}
    </th>
  ),
  td: ({ children }) => <td className="border border-border px-2 py-1 align-top">{children}</td>,
};

export interface MarkdownProps {
  /** Raw markdown string (may be partial while streaming). */
  children: string;
  className?: string;
  /**
   * Optional decorator for **bold** runs whose text is a single known token
   * (e.g. a brand/hero name). Return a node to REPLACE the default bold render
   * (e.g. an icon + label), or null to keep the default. Keeps this component
   * generic — the caller owns any domain vocabulary (hero icons, etc.).
   */
  decorateStrong?: (text: string) => ReactNode | null;
  /**
   * True while the assistant is still streaming this text. When set, committed
   * blocks are frozen and only the last (still-arriving) block re-parses per
   * token — removing the "renders raw, then reflows" flicker. When the turn
   * settles (false), the whole string is parsed once as a single clean pass.
   */
  isStreaming?: boolean;
}

/**
 * Render assistant markdown. Wrap in your own text-size/color container — the
 * element map inherits color and sets only structure + relative sizing.
 */
export function Markdown({ children, className, decorateStrong, isStreaming }: MarkdownProps) {
  const components = useMemo<Components>(() => {
    if (!decorateStrong) return COMPONENTS;
    return {
      ...COMPONENTS,
      strong: ({ children: c }) => {
        const text =
          typeof c === 'string'
            ? c
            : Array.isArray(c) && c.length === 1 && typeof c[0] === 'string'
              ? c[0]
              : null;
        const decorated = text ? decorateStrong(text) : null;
        return decorated ?? <strong className="font-semibold">{c}</strong>;
      },
    };
  }, [decorateStrong]);

  // While streaming, freeze the committed prefix so only the live tail re-parses
  // per token. Settled turns render the whole string in one pass (always correct).
  const { stable, tail } = useMemo(
    () => (isStreaming ? splitStreamingMarkdown(children) : { stable: '', tail: children }),
    [isStreaming, children]
  );

  return (
    <div className={cn('text-sm break-words', className)}>
      {stable && <MarkdownBody content={stable} components={components} />}
      <MarkdownBody content={tail} components={components} />
    </div>
  );
}
